/**
 * Pilot **Reload** (RAW Core p.75) — the on-foot pilot's own
 * Quick action: "you reload one Pilot Weapon with the LOADING tag, making it usable again." Pure
 * rules (no I/O, no dice — fully deterministic once the target weapon is resolved): mirrors
 * `pilotBridge.ts`'s "thin legality-check + step-builder the room composes with the
 * action-economy spend" shape, not `stabilize.ts`'s bundled primary/secondary one (Reload is its
 * own single-purpose action here, not a Stabilize secondary).
 *
 * **Uses the shared Loading shape verbatim**: `weaponInstanceKey`/`loadedResourceKey`/
 * `isWeaponLoaded` from `@lancer-vtt/domain` — never re-derives the `loaded:<key>` string by
 * hand. Where this differs from `stabilize.ts`'s own mech-Stabilize `reload` secondary: that one
 * `resource_set`s the key to an explicit `1` (leaves a residue — fine there, Stabilize never
 * cares about byte-identity to a pristine weapon). This module instead reuses the {@link
 * import("@lancer-vtt/domain").ResourceClearStep} grounded primitive DIRECTLY (`reloadSteps`),
 * restoring TRUE absence — a pilot's Reload undo/redo round-trips to a state byte-identical to a
 * fresh, never-fired weapon (see `steps.ts`'s `resource_clear` doc).
 *
 * If a Fight against an unloaded weapon needs the same reject Skirmish/Barrage get, that's
 * `economy.ts`'s `checkLoaded` — this module never forks a pilot-specific fire gate.
 */

import type { Combatant, CombatantId, EncounterState, ResolvedStep, WeaponProfile } from "@lancer-vtt/domain";
import { isWeaponLoaded, loadedResourceKey, weaponInstanceKey } from "@lancer-vtt/domain";
import { ok, err, type Result } from "@lancer-vtt/shared";

/** The resolved outcome of a legal Reload — the pilot combatant and the ONE Loading weapon it will reload. */
export type ReloadTarget = {
  actor: Combatant;
  weapon: WeaponProfile;
};

/**
 * May `actorId` (an on-foot pilot) Reload right now (RAW p.75, a Quick action)? Checks, in
 * order: the actor exists and is a `pilot`-kind combatant (mechs
 * reload Loading weapons via Stabilize, not this action); the named `weaponId`
 * ({@link weaponInstanceKey}) — or, if omitted, the actor's FIRST Loading weapon — resolves to a
 * real Loading-tagged entry on `base.weapons`; that weapon is actually unloaded right now
 * ({@link isWeaponLoaded} — an already-loaded weapon has nothing to reload, a hard reject rather
 * than a silent no-op, mirroring Dismount/Eject/Mount's own "reject, don't degrade" bridge
 * discipline). On success, returns the resolved {@link ReloadTarget} so the caller (the room)
 * never re-derives which weapon this is.
 */
export function checkReloadLegality(
  state: EncounterState,
  actorId: CombatantId,
  weaponId?: string,
): Result<ReloadTarget, string> {
  const actor = state.combatants[actorId];
  if (!actor) return err(`reload: no such combatant ${actorId}`);
  if (actor.kind !== "pilot") return err(`reload: ${actor.name} is not an on-foot pilot (a mech reloads Loading weapons via Stabilize)`);
  const loadingWeapons = (actor.base.weapons ?? []).filter((w) => w.loading);
  let weapon: WeaponProfile | undefined;
  if (weaponId !== undefined) {
    weapon = loadingWeapons.find((w) => weaponInstanceKey(w) === weaponId);
    if (!weapon) return err(`reload: ${actor.name} carries no Loading weapon matching "${weaponId}"`);
  } else {
    weapon = loadingWeapons[0];
    if (!weapon) return err(`reload: ${actor.name} carries no Loading weapon`);
  }
  if (isWeaponLoaded(actor.current.resources, weaponInstanceKey(weapon))) {
    return err(`reload: ${actor.name}'s ${weapon.name} is already loaded — nothing to reload`);
  }
  return ok({ actor, weapon });
}

/**
 * The recorded step list for a legal Reload ({@link checkReloadLegality} already confirmed the
 * target weapon is unloaded — i.e. its `loaded:<key>` resource is genuinely PRESENT, since
 * {@link isWeaponLoaded}'s `?? 1` default only reads "unloaded" off an explicit value): a single
 * `resource_clear` that deletes the key entirely, restoring TRUE absence (see the module doc for
 * why this differs from `stabilize.ts`'s `resource_set val:1`). `restoreVal` is the weapon's
 * current explicit resource value (always present here, per the legality gate), so undoing
 * Reload recreates the exact pre-Reload unloaded state, and redoing it clears again.
 */
export function reloadSteps(target: ReloadTarget): ResolvedStep[] {
  const { actor, weapon } = target;
  const key = loadedResourceKey(weaponInstanceKey(weapon));
  const restoreVal = actor.current.resources?.[key] ?? 0;
  return [{ op: "resource_clear", target: actor.id, key, restoreVal }];
}
